import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Grid, Button, Stack, IconButton, Tooltip, InputBase, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete, MenuItem, InputAdornment, Snackbar, Alert, Switch,
} from '@mui/material';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import { useSelector } from 'react-redux';
import { selectEffectiveModules } from '../../redux/slices/authSlice';
import { hasModule } from '../../data/serverModules';
import { selectActiveEntity, selectActiveEntityId, ALL_ENTITY_ID } from '../../redux/slices/orgSlice';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_DARK, PRIMARY_BORDER } from '../../theme';
import { inr } from '../../utils/format';
import SearchSelect from '../SearchSelect';
import { solidBtn, ghostBtn, field, Panel, EmptyState, SkeletonCards, ConfirmDialog, LoadAlert } from '../uiKit';
import http, { apiErrorMessage } from '../../Api/http';
import { GetVacancies, CreateVacancy, UpdateVacancy, SetVacancyStatus, DeleteVacancy, GetDepartments, GetDesignations, SetVacancyPublished, GetCareersSettings, getEmployees } from '../../Api/Api';
import CareersLinks, { buildLinks } from './CareersLinks';
import VacancyCard from './VacancyCard';
import { readCategories, readVacancyCategories, saveVacancyCategory } from './CategoriesTab';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';

// One hue throughout — priority is carried by depth of the brand purple rather
// than by three unrelated colours competing on the same card.
const PRIORITIES = [
    { key: 'High', color: '#fff', bg: PRIMARY_DARK, tint: PRIMARY_DARK },
    { key: 'Medium', color: PRIMARY, bg: PRIMARY_LIGHT, tint: PRIMARY },
    { key: 'Low', color: '#6B7280', bg: '#F1F5F9', tint: PRIMARY_BORDER },
];
const TYPES = ['Permanent', 'Contract', 'Internship', 'Part-Time'];
const DESC_MAX = 4000;
const EXPERIENCE = ['0-1 yrs', '1-3 yrs', '2-4 yrs', '4-7 yrs', '5-8 yrs', '8+ yrs'];

// Ten fields in one flat stack read as a wall; these break the form into the
// three questions it actually asks.
function FormSection({ children }) {
    return (
        <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', mt: 0.4 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: '#6E6B99', textTransform: 'uppercase', letterSpacing: 0.6, whiteSpace: 'nowrap' }}>
                {children}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: '#EEF0F6' }} />
        </Stack>
    );
}

const EMPTY = {
    title: '', department: '', designation: '', openings: 1, experience: '1-3 yrs',
    location: '', employmentType: 'Permanent', minSalary: '', maxSalary: '', salaryLabel: '',
    priority: 'Medium', hiringManager: '', description: '', publish: false, active: true, category: '',
};

const normalizeVacancy = (v) => ({
    id: v.id,
    title: v.roleTitle ?? '',
    department: v.department ?? '',
    designation: v.designation ?? '',
    employmentType: v.employmentType ?? '',
    priority: v.priority ?? 'Medium',
    status: v.status ?? 'Open',
    openings: Number(v.openings) || 0,
    filled: Number(v.filled) || 0,
    applicants: Number(v.applicants) || 0,
    experience: v.experienceRange ?? '',
    minSalary: Number(v.minSalary) || 0,
    maxSalary: Number(v.maxSalary) || 0,
    salaryLabel: v.salaryLabel ?? '',
    location: v.location ?? '',
    hiringManager: v.hiringManager ?? '',
    description: v.description ?? '',
    postedOn: v.postedOn ?? '',
    closedOn: v.closedOn ?? '',
    isPublished: Boolean(v.isPublished ?? v.isPublishedOnCareersPage ?? v.publishedOnCareersPage ?? v.onCareersPage) || Boolean(v.jobBoardLinks),
    publicKey: v.publicKey ?? '',
    jobBoardLinks: v.jobBoardLinks || null,
});

const namesFrom = (settled, key, activeEntityId) => {
    const body = settled.status === 'fulfilled' ? settled.value?.data : null;
    if (!body || body.error) return [];
    const rows = Array.isArray(body?.data?.[key]) ? body.data[key] : [];
    const scoped = activeEntityId === ALL_ENTITY_ID
        ? rows
        : rows.filter((r) => String(r.entityId) === String(activeEntityId));
    return [...new Set(
        scoped.filter((r) => r.isActive !== false).map((r) => (r.name || '').trim()).filter(Boolean),
    )].sort((a, b) => a.localeCompare(b));
};

// Search box published to the breadcrumb row; it keeps its own text so typing doesn't re-publish the row.
function CrumbSearch({ initial, onSearch }) {
    const [text, setText] = useState(initial || '');
    return (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', px: 1.4, height: 42, width: { xs: 180, sm: 320 } }}>
            <SearchRoundedIcon sx={{ fontSize: 18, color: '#98A0AE' }} />
            <InputBase
                placeholder="Search roles…" value={text}
                onChange={(e) => { setText(e.target.value); onSearch(e.target.value); }}
                sx={{ fontSize: 13, flex: 1 }}
            />
        </Stack>
    );
}

export default function VacanciesTab({ setActions }) {
    const entity = useSelector(selectActiveEntity);
    const activeEntityId = useSelector(selectActiveEntityId);

    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(true);
    const [employees, setEmployees] = useState([]); // everyone who can be assigned to a vacancy
    useEffect(() => {
        let live = true;
        http.get(getEmployees).then(({ data: body }) => {
            if (!live) return;
            const list = Array.isArray(body?.data) ? body.data : [];
            setEmployees(list.map((e) => ({ name: String(e.name ?? '').trim(), code: e.employeeCode ?? e.rollNumber ?? '' })).filter((e) => e.name).sort((a, b) => a.name.localeCompare(b.name)));
        }).catch(() => { if (live) setEmployees([]); });
        return () => { live = false; };
    }, [activeEntityId]);

    const [vacancies, setVacancies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [statusBusy, setStatusBusy] = useState(null);

    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [deptFilter, setDeptFilter] = useState('all');
    const [desigFilter, setDesigFilter] = useState('all');
    const [expFilter, setExpFilter] = useState('all');
    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [more, setMore] = useState([]); // extra openings under the same job category
    const [tried, setTried] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [snack, setSnack] = useState(null);
    const [careers, setCareers] = useState(null);
    const [links, setLinks] = useState({});
    const [pubBusy, setPubBusy] = useState(null);
    const [pubError, setPubError] = useState({});
    const [created, setCreated] = useState(null);
    const [pubOverride, setPubOverride] = useState({}); // remembers a toggle when the list response lacks a published flag

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const loadVacancies = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetVacancies);
            if (body?.error) throw new Error(body.message || 'Could not load vacancies.');
            const list = Array.isArray(body?.data?.vacancies) ? body.data.vacancies : [];
            setVacancies(list.map(normalizeVacancy));
            setLoadError('');
        } catch (err) {
            setLoadError(apiErrorMessage(err, 'Could not load vacancies.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadVacancies(); }, [loadVacancies]);

    const loadCareers = useCallback(async () => {
        try {
            const { data: body } = await http.get(GetCareersSettings);
            setCareers(body?.error ? null : body.data);
        } catch { setCareers(null); }
    }, []);
    useEffect(() => { loadCareers(); }, [loadCareers, activeEntityId]);

    const linksFor = (v) => links[v.id]
        || (v.jobBoardLinks ? { liveUrl: v.jobBoardLinks.careersLiveUrl, linkedIn: v.jobBoardLinks.linkedIn, naukri: v.jobBoardLinks.naukri } : null)
        || buildLinks(careers, v.publicKey);
    const siteSet = Boolean(careers?.siteBaseUrl);
    // Publishing needs the Careers Settings module; without it vacancies can still be created, just not published.
    const canPublish = hasModule(useSelector(selectEffectiveModules), 'careers-settings');
    const editing = (() => {
        if (dialog?.mode !== 'edit') return null;
        const v = vacancies.find((x) => x.id === dialog.id);
        return v ? (v.id in pubOverride ? { ...v, isPublished: pubOverride[v.id] && v.status !== 'Closed' } : v) : null;
    })();

    const togglePublish = async (v) => {
        if (pubBusy) return;
        const next = !v.isPublished;
        setPubBusy(v.id);
        setPubError((e) => ({ ...e, [v.id]: '' }));
        try {
            const { data: body } = await http.put(SetVacancyPublished, { id: v.id, isPublished: next });
            if (body?.error) throw new Error(body.message || 'Could not update the careers page.');
            const d = body?.data || body || {};
            setPubOverride((m) => ({ ...m, [v.id]: next }));
            if (next) {
                setLinks((m) => ({ ...m, [v.id]: { liveUrl: d.liveUrl, linkedIn: d.jobBoardLinks?.linkedIn, naukri: d.jobBoardLinks?.naukri } }));
                if (d.verified === false) notify('Published, but not visible yet. Check that its status is Open.', 'warning');
                else if (d.siteConfigured === false) notify('Set your website address in Careers settings to get shareable links.', 'warning');
                else notify(v.title + ' is live on the careers page.');
            } else {
                setLinks((m) => { const rest = { ...m }; delete rest[v.id]; return rest; });
                notify(v.title + ' removed from the careers page.');
            }
            await Promise.all([loadVacancies(), loadCareers()]);
        } catch (err) {
            const msg = apiErrorMessage(err, 'Could not update the careers page.');
            setPubError((e) => ({ ...e, [v.id]: msg }));
            notify(msg, 'error');
        } finally {
            setPubBusy(null);
        }
    };

    const loadOrgOptions = useCallback(async () => {
        setOptionsLoading(true);
        const [deptRes, desigRes] = await Promise.allSettled([
            http.get(GetDepartments),
            http.get(GetDesignations),
        ]);
        setDepartments(namesFrom(deptRes, 'departments', activeEntityId));
        setDesignations(namesFrom(desigRes, 'designations', activeEntityId));
        setOptionsLoading(false);
    }, [activeEntityId]);

    useEffect(() => { loadOrgOptions(); }, [loadOrgOptions]);

    // Search, New Vacancy and Import sit at the right end of the breadcrumb row, published up to the page.
    // Handlers go through a ref so the published buttons are not rebuilt on every render.
    const actionRef = useRef({});
    actionRef.current = { create: () => openCreate() };
    const savedSearch = useRef(q);
    useEffect(() => {
        if (!setActions) return undefined;
        setActions(
            <>
                <CrumbSearch initial={savedSearch.current} onSearch={setQ} />
                <Button startIcon={<AddRoundedIcon />} onClick={() => actionRef.current.create()} sx={{ ...solidBtn, height: 42, px: 1.8, fontSize: 13 }}>New Vacancy</Button>
            </>,
        );
        return () => setActions(null);
    }, [setActions]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        return vacancies.map((v) => (v.id in pubOverride ? { ...v, isPublished: pubOverride[v.id] && v.status !== 'Closed' } : v)).filter((v) => {
            if (statusFilter !== 'all' && v.status !== statusFilter) return false;
            if (deptFilter !== 'all' && v.department !== deptFilter) return false;
            if (desigFilter !== 'all' && v.designation !== desigFilter) return false;
            if (expFilter !== 'all' && v.experience !== expFilter) return false;
            if (!s) return true;
            return [v.title, v.department, v.designation, v.location, v.hiringManager].some((x) => String(x || '').toLowerCase().includes(s));
        });
    }, [vacancies, pubOverride, q, statusFilter, deptFilter, desigFilter, expFilter]);

    // Filter choices come from the vacancies themselves, so every option matches at least one card.
    const uniq = (key) => [...new Set(vacancies.map((v) => v[key]).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    const deptChoices = useMemo(() => uniq('department'), [vacancies]);
    const desigChoices = useMemo(() => uniq('designation'), [vacancies]);
    const expChoices = useMemo(() => {
        const present = new Set(vacancies.map((v) => v.experience).filter(Boolean));
        return [...EXPERIENCE.filter((e) => present.has(e)), ...[...present].filter((e) => !EXPERIENCE.includes(e))];
    }, [vacancies]);
    const anyFilter = statusFilter !== 'all' || deptFilter !== 'all' || desigFilter !== 'all' || expFilter !== 'all';
    const clearFilters = () => { setStatusFilter('all'); setDeptFilter('all'); setDesigFilter('all'); setExpFilter('all'); };

    const departmentOptions = useMemo(() => (
        form.department && !departments.includes(form.department) ? [...departments, form.department] : departments
    ), [departments, form.department]);
    const designationOptions = useMemo(() => (
        form.designation && !designations.includes(form.designation) ? [...designations, form.designation] : designations
    ), [designations, form.designation]);

    const categoryOptions = useMemo(() => {
        const names = [...readCategories(activeEntityId).map((c) => c.name), ...vacancies.map((v) => v.title)].map((n) => String(n).trim()).filter(Boolean);
        if (form.category) names.push(form.category);
        return [...new Set(names)].sort((a, b) => a.localeCompare(b));
    }, [activeEntityId, vacancies, form.category, dialog]); // eslint-disable-line react-hooks/exhaustive-deps
    const updMore = (i) => (k) => (e) => setMore((m) => m.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
    const addOpening = () => setMore((m) => [...m, { ...EMPTY, category: form.category, title: form.title, department: form.department, designation: form.designation, location: form.location, employmentType: form.employmentType, hiringManager: form.hiringManager }]);

    const openCreate = () => { setForm(EMPTY); setMore([]); setTried(false); setDialog({ mode: 'create' }); };
    const openEdit = (v) => {
        setForm({ ...EMPTY, ...v, category: readVacancyCategories(activeEntityId)[v.id] || '', minSalary: String(v.minSalary || ''), maxSalary: String(v.maxSalary || '') });
        setMore([]);
        setTried(false);
        setDialog({ mode: 'edit', id: v.id });
    };
    const closeDialog = () => { if (!saving) setDialog(null); };
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const openingsNum = Number(form.openings) || 0;


    const blockValid = (f) => String(f.title).trim() && f.department && (Number(f.openings) || 0) > 0
        && !((Number(f.minSalary) || 0) > 0 && (Number(f.maxSalary) || 0) > 0 && Number(f.maxSalary) < Number(f.minSalary))
        && (f.description || '').length <= DESC_MAX;
    const payloadOf = (f) => ({
        roleTitle: f.title.trim(), openings: Number(f.openings) || 0, department: f.department, designation: f.designation || '',
        experienceRange: f.experience, employmentType: f.employmentType, priority: f.priority,
        minSalary: Number(f.minSalary) || 0, maxSalary: Number(f.maxSalary) || 0, salaryLabel: (f.salaryLabel || '').trim(),
        location: f.location || '', hiringManager: f.hiringManager || '', description: f.description || '',
    });

    const submit = async () => {
        setTried(true);
        const isEdit = dialog?.mode === 'edit';
        if (!isEdit && (!activeEntityId || activeEntityId === ALL_ENTITY_ID)) {
            notify('Pick a single business entity in the sidebar before opening a vacancy.', 'warning');
            return;
        }
        const blocks = isEdit ? [form] : [form, ...more.map((m) => ({ ...m, title: form.title }))];
        const bad = blocks.findIndex((f) => !blockValid(f));
        if (bad >= 0) {
            const f = blocks[bad];
            const where = blocks.length > 1 ? ` (opening ${bad + 1})` : '';
            notify((!String(f.title).trim() ? 'Give the role a title.' : !f.department ? 'Pick the department this role sits in.' : (Number(f.openings) || 0) <= 0 ? 'A vacancy needs at least one opening.' : 'Check the salary range and description.') + where, 'warning');
            return;
        }

        setSaving(true);
        try {
            if (isEdit) {
                const { data: body } = await http.put(UpdateVacancy, { id: dialog.id, ...payloadOf(form) });
                if (body?.error) throw new Error(body.message || 'Could not save the vacancy.');
                saveVacancyCategory(activeEntityId, dialog.id, form.category);
                notify(body?.message || `${form.title.trim()} updated.`);
                setDialog(null);
                await Promise.all([loadVacancies(), loadCareers()]);
                return;
            }
            // One vacancy per opening block; each keeps its own active switch and careers-page choice.
            let made = 0; let lastCareers = null;
            for (const f of blocks) {
                const payload = payloadOf(f);
                const wantPublish = canPublish && f.publish && f.active !== false;
                if (wantPublish) payload.publishOnCareersPage = true;
                const { data: body } = await http.post(CreateVacancy, payload);
                if (body?.error) throw new Error(body.message || 'Could not save the vacancy.');
                made += 1;
                const id = body?.data?.id;
                if (id != null) {
                    saveVacancyCategory(activeEntityId, id, form.category);
                    if (f.active === false) await http.put(SetVacancyStatus, { id, status: 'Closed' });
                    if (wantPublish && body?.careers?.published) setPubOverride((m) => ({ ...m, [id]: true }));
                }
                if (wantPublish && body?.careers) lastCareers = { title: payload.roleTitle, ...body.careers };
            }
            notify(made > 1 ? `${made} vacancies opened under ${form.category || form.title.trim()}.` : `${form.title.trim()} opened.`);
            setDialog(null); setMore([]);
            if (made === 1 && lastCareers) setCreated(lastCareers);
            await Promise.all([loadVacancies(), loadCareers()]);
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not save the vacancy.'), 'error');
            await loadVacancies();
        } finally {
            setSaving(false);
        }
    };

    const setStatus = async (v, status) => {
        if (statusBusy) return;
        setStatusBusy(v.id);
        try {
            const { data: body } = await http.put(SetVacancyStatus, { id: v.id, status });
            if (body?.error) throw new Error(body.message || 'Could not update the vacancy.');
            notify(body?.message || `${v.title} ${status === 'Closed' ? 'closed' : 'reopened'}.`);
            await loadVacancies();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not update the vacancy.'), 'error');
        } finally {
            setStatusBusy(null);
        }
    };

    // One opening's fields. The first uses the main form; extra openings under the same job category reuse the same layout.
    const fieldsFor = (f, upd, idx) => {
        const oN = Number(f.openings) || 0;
        const bad = (Number(f.minSalary) || 0) > 0 && (Number(f.maxSalary) || 0) > 0 && Number(f.maxSalary) < Number(f.minSalary);
        const long = (f.description || '').length > DESC_MAX;
        return (
            <>
                        <Grid size={12}><FormSection>The role</FormSection></Grid>
                        {idx === 0 && (<Grid size={{ xs: 12, sm: 8 }}>
                            <TextField label="Role title" size="small" fullWidth value={f.title} onChange={upd('title')}
                                error={tried && !f.title.trim()} placeholder="e.g. Senior React Engineer"
                                slotProps={{ inputLabel: { shrink: true } }} sx={field} />
                        </Grid>)}
                        <Grid size={{ xs: 12, sm: idx === 0 ? 4 : 12 }}>
                            <TextField label="Openings" type="number" size="small" fullWidth value={f.openings} onChange={upd('openings')}
                                error={tried && oN <= 0}
                                slotProps={{ htmlInput: { min: 1 }, inputLabel: { shrink: true } }} sx={field} />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography sx={{ fontSize: 12, color: '#64748B', mb: 0.5 }}>Department</Typography>
                            <SearchSelect
                                value={f.department} onChange={upd('department')} disabled={optionsLoading} error={tried && !f.department}
                                searchPlaceholder="Search departments…" sx={field}
                                options={[{ value: '', label: 'Select department', muted: true }, ...departmentOptions.map((d) => ({ value: d, label: d }))]}
                            />
                            <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.5 }}>
                                {optionsLoading ? 'Loading departments…' : departmentOptions.length === 0 ? 'No departments in this entity yet — add one under Organisation.' : null}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography sx={{ fontSize: 12, color: '#64748B', mb: 0.5 }}>Designation</Typography>
                            <SearchSelect
                                value={f.designation} onChange={upd('designation')} disabled={optionsLoading}
                                searchPlaceholder="Search designations…" sx={field}
                                options={[{ value: '', label: 'Select designation', muted: true }, ...designationOptions.map((d) => ({ value: d, label: d }))]}
                            />
                            <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.5 }}>
                                {optionsLoading ? 'Loading designations…' : designationOptions.length === 0 ? 'No designations in this entity yet — add one under Organisation.' : null}
                            </Typography>
                        </Grid>

                        <Grid size={12}><FormSection>Requirements</FormSection></Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField select label="Experience" size="small" fullWidth value={f.experience} onChange={upd('experience')}
                                slotProps={{ inputLabel: { shrink: true } }} sx={field}>
                                {EXPERIENCE.map((e) => <MenuItem key={e} value={e} sx={{ fontSize: 13.5 }}>{e}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField select label="Type" size="small" fullWidth value={f.employmentType} onChange={upd('employmentType')}
                                slotProps={{ inputLabel: { shrink: true } }} sx={field}>
                                {TYPES.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: 13.5 }}>{t}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField select label="Priority" size="small" fullWidth value={f.priority} onChange={upd('priority')}
                                slotProps={{ inputLabel: { shrink: true } }} sx={field}>
                                {PRIORITIES.map((p) => <MenuItem key={p.key} value={p.key} sx={{ fontSize: 13.5 }}>{p.key}</MenuItem>)}
                            </TextField>
                        </Grid>

                        <Grid size={12}><FormSection>Package &amp; location</FormSection></Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Minimum salary (per year)" type="number" size="small" fullWidth value={f.minSalary} onChange={upd('minSalary')}
                                placeholder="0"
                                slotProps={{ input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> }, inputLabel: { shrink: true } }} sx={field} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Maximum salary (per year)" type="number" size="small" fullWidth value={f.maxSalary} onChange={upd('maxSalary')}
                                error={tried && bad} helperText={bad ? 'Must be at least the minimum.' : undefined}
                                placeholder="0"
                                slotProps={{ input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> }, inputLabel: { shrink: true } }} sx={field} />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Location" size="small" fullWidth value={f.location} onChange={upd('location')}
                                placeholder="e.g. Chennai"
                                slotProps={{ input: { startAdornment: <InputAdornment position="start"><PlaceRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} /></InputAdornment> }, inputLabel: { shrink: true } }} sx={field} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Autocomplete
                                size="small" fullWidth autoHighlight
                                options={[...new Set([...(f.hiringManager ? [f.hiringManager] : []), ...employees.map((e) => e.name)])]}
                                value={f.hiringManager || null}
                                onChange={(_, v) => upd('hiringManager')({ target: { value: v || '' } })}
                                noOptionsText="No employees found"
                                renderInput={(params) => (
                                    <TextField {...params} label="Assign employee" placeholder="Select employee"
                                        slotProps={{ ...params.slotProps, inputLabel: { ...params.slotProps?.inputLabel, shrink: true } }} sx={field} />
                                )}
                            />
                        </Grid>

                        <Grid size={12}>
                            <TextField label="Description (optional)" size="small" fullWidth multiline minRows={2} value={f.description} onChange={upd('description')}
                                placeholder="What will this person own?"
                                error={long}
                                helperText={`${(f.description || '').length} / ${DESC_MAX}${long ? ' - too long, shorten it to save.' : ''}`}
                                slotProps={{ inputLabel: { shrink: true } }} sx={field} />
                        </Grid>

                        {dialog?.mode !== 'edit' && (
                            <Grid size={12}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.4 }}>
                                    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1.2, px: 1.6, borderRadius: '10px', bgcolor: f.active !== false ? '#F0FDF4' : '#F8FAFC', border: `1px solid ${f.active !== false ? '#BBF7D0' : '#EEF1F6'}` }}>
                                        <Box>
                                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0F172A' }}>{f.active !== false ? 'Active' : 'Inactive'}</Typography>
                                            <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>{f.active !== false ? 'Open for hiring' : 'Saved but closed'}</Typography>
                                        </Box>
                                        <Switch checked={f.active !== false} onChange={(e) => upd('active')({ target: { value: e.target.checked } })} />
                                    </Stack>
                                    {canPublish && (
                                        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1.2, px: 1.6, borderRadius: '10px', bgcolor: '#F8FAFC', border: '1px solid #EEF1F6' }}>
                                            <Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0F172A' }}>Careers page</Typography>
                                                <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>{f.active === false ? 'Needs an active vacancy' : f.publish ? 'Shown as its own listing' : 'Not published'}</Typography>
                                            </Box>
                                            <Switch checked={Boolean(f.publish) && f.active !== false} disabled={f.active === false} onChange={(e) => upd('publish')({ target: { value: e.target.checked } })} />
                                        </Stack>
                                    )}
                                </Box>
                            </Grid>
                        )}
            </>
        );
    };

    const doDelete = async () => {
        if (!confirm || deleting) return;
        setDeleting(true);
        try {
            const { data: body } = await http.delete(DeleteVacancy, { params: { id: confirm.id } });
            if (body?.error) throw new Error(body.message || 'Could not remove the vacancy.');
            notify(body?.message || `${confirm.title} removed.`);
            setConfirm(null);
            await loadVacancies();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not remove the vacancy.'), 'error');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ p: 2, pt: 1.5 }}>
            <Panel
                title="Vacancies"
                icon={WorkOutlineRoundedIcon}
                chip={`${filtered.length} of ${vacancies.length}`}
                action={(
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ width: 160 }}>
                            <SearchSelect
                                value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} searchPlaceholder="Search departments…"
                                sx={{ ...field, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}
                                options={[{ value: 'all', label: 'All departments' }, ...deptChoices.map((d) => ({ value: d, label: d }))]}
                            />
                        </Box>
                        <Box sx={{ width: 160 }}>
                            <SearchSelect
                                value={desigFilter} onChange={(e) => setDesigFilter(e.target.value)} searchPlaceholder="Search designations…"
                                sx={{ ...field, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}
                                options={[{ value: 'all', label: 'All designations' }, ...desigChoices.map((d) => ({ value: d, label: d }))]}
                            />
                        </Box>
                        <Box sx={{ width: 150 }}>
                            <SearchSelect
                                value={expFilter} onChange={(e) => setExpFilter(e.target.value)} searchPlaceholder="Search experience…"
                                sx={{ ...field, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}
                                options={[{ value: 'all', label: 'All experience' }, ...expChoices.map((d) => ({ value: d, label: d }))]}
                            />
                        </Box>
                        <Box sx={{ width: 140 }}>
                            <SearchSelect
                                value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} searchPlaceholder="Search status…"
                                sx={{ ...field, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}
                                options={[{ value: 'all', label: 'All statuses' }, { value: 'Open', label: 'Open' }, { value: 'Closed', label: 'Closed' }]}
                            />
                        </Box>
                        {anyFilter && <Button onClick={clearFilters} sx={{ ...ghostBtn, height: 38, px: 1.6, fontSize: 12.5 }}>Clear</Button>}
                        <Tooltip arrow title="Reload">
                            <IconButton onClick={loadVacancies} disabled={loading} sx={{ border: '1px solid #E6EAF1', bgcolor: '#fff', borderRadius: '7px', color: '#64748B', height: 38, width: 38, '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )}
            >
                {loading ? (
                    <SkeletonCards count={6} height={168} size={{ xs: 12, md: 6, xl: 4 }} />
                ) : loadError ? (
                    <Box sx={{ p: 3 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }}
                            action={<Button size="small" onClick={loadVacancies} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>
                            {loadError}
                        </LoadAlert>
                    </Box>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        icon={WorkOutlineRoundedIcon}
                        title={q || anyFilter ? 'No vacancies match these filters' : 'No vacancies yet'}
                        hint={q || anyFilter ? 'Clear the search or change a filter.' : 'Open a role, add candidates against it, then schedule their interviews.'}
                    />
                ) : (
                    <Box sx={{ p: 1.8 }}>
                        <Grid container spacing={2.5}>
                            {filtered.map((v) => (
                                <Grid size={{ xs: 12, md: 6, xl: 4 }} key={v.id}>
                                    <VacancyCard
                                        v={v}
                                        prio={PRIORITIES.find((p) => p.key === v.priority) || PRIORITIES[1]}
                                        links={linksFor(v)}
                                        siteSet={siteSet}
                                        canPublish={canPublish}
                                        pubBusy={pubBusy}
                                        pubError={pubError[v.id]}
                                        statusBusy={statusBusy}
                                        salary={v.salaryLabel || salaryBand(v)}
                                        onEdit={() => openEdit(v)}
                                        onDelete={() => setConfirm(v)}
                                        onTogglePublish={() => togglePublish(v)}
                                        onStatus={(st) => setStatus(v, st)}
                                        onCopied={() => notify('Link copied.')}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Panel>

            <Dialog open={Boolean(dialog)} onClose={closeDialog} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 1.6 }}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '11px', flexShrink: 0,
                            bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <WorkOutlineRoundedIcon sx={{ fontSize: 20, color: PRIMARY }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>{dialog?.mode === 'edit' ? 'Edit Vacancy' : 'New Vacancy'}</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>
                                Opened under <strong>{entity?.name || '—'}</strong>. Candidates and interviews hang off this role.
                            </Typography>
                        </Box>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ pt: '10px !important' }} dividers>
                    <Grid container spacing={1.8}>
                        <Grid size={12}>
                            <Typography sx={{ fontSize: 12, color: '#64748B', mb: 0.5 }}>Job category</Typography>
                            <SearchSelect
                                value={form.category} onChange={(e) => { const c = e.target.value; setForm((f) => ({ ...f, category: c, title: f.title.trim() ? f.title : c })); }}
                                searchPlaceholder="Search job categories…" sx={field}
                                options={[{ value: '', label: 'Select job category', muted: true }, ...categoryOptions.map((c) => ({ value: c, label: c }))]}
                            />
                        </Grid>
                        {more.length > 0 && <Grid size={12}><Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: PRIMARY_LIGHT, color: PRIMARY_DARK, fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</Box><Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#0F172A' }}>Opening 1</Typography></Stack></Grid>}
                        {fieldsFor(form, set, 0)}
                        {canPublish && dialog?.mode === 'edit' && editing && (
                            <Grid size={12}>
                                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1.2, px: 1.6, borderRadius: '8px', bgcolor: '#F8FAFC', border: '1px solid #EEF1F6' }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>On careers page</Typography>
                                        <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>
                                            {editing.status === 'Closed' ? 'Only an open vacancy can be published.'
                                                : !siteSet && !editing.isPublished ? 'Set the website address first.'
                                                    : editing.isPublished ? 'Live now. Applies immediately, no need to save.' : 'Off. Applies immediately, no need to save.'}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                                        {pubBusy === editing.id && <CircularProgress size={14} />}
                                        <Switch checked={editing.isPublished} onChange={() => togglePublish(editing)}
                                            disabled={editing.status === 'Closed' || pubBusy === editing.id || (!siteSet && !editing.isPublished)} />
                                    </Stack>
                                </Stack>
                                {pubError[editing.id] && <Typography sx={{ fontSize: 11.5, color: '#E11D48', mt: 0.5 }}>{pubError[editing.id]}</Typography>}
                            </Grid>
                        )}

                        {dialog?.mode === 'edit' && editing && (
                            <Grid size={12}>
                                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1.2, px: 1.6, borderRadius: '8px', bgcolor: '#F8FAFC', border: '1px solid #EEF1F6' }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{editing.status === 'Closed' ? 'Inactive' : 'Active'}</Typography>
                                        <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>Applies immediately. The careers page choice above is separate.</Typography>
                                    </Box>
                                    <Switch checked={editing.status !== 'Closed'} disabled={Boolean(statusBusy)} onChange={(e) => setStatus(editing, e.target.checked ? 'Open' : 'Closed')} />
                                </Stack>
                            </Grid>
                        )}
                        {dialog?.mode === 'edit' && (
                            <Grid size={12}>
                                <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', p: 1.4, borderRadius: '8px', bgcolor: '#F8FAFC', border: '1px solid #EEF1F6' }}>
                                    <GroupsRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} />
                                    <Typography sx={{ fontSize: 12, color: '#64748B' }}>
                                        <strong>{form.filled ?? 0}</strong> of {openingsNum} already filled. Positions fill when a selected candidate is marked as joined — not from this form.
                                    </Typography>
                                </Stack>
                            </Grid>
                        )}
                    </Grid>
                    {more.map((m, i) => (
                        <Box key={i} sx={{ mt: 2.5, p: 2, borderRadius: '12px', border: '1px solid #E6EAF1', bgcolor: '#FBFBFE' }}>
                            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: PRIMARY_LIGHT, color: PRIMARY_DARK, fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 2}</Box><Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#0F172A' }}>Opening {i + 2}</Typography></Stack>
                                <Tooltip arrow title="Remove this opening"><IconButton size="small" onClick={() => setMore((x) => x.filter((_, j) => j !== i))}><ClearRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                            </Stack>
                            <Grid container spacing={1.8}>{fieldsFor(m, updMore(i), i + 1)}</Grid>
                        </Box>
                    ))}
                    {dialog?.mode !== 'edit' && (
                        <Button fullWidth startIcon={<AddRoundedIcon />} onClick={addOpening} sx={{ ...ghostBtn, height: 44, mt: 2.5, borderStyle: 'dashed', borderColor: PRIMARY_BORDER, color: PRIMARY, bgcolor: '#FBFAFF', '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY } }}>Add another opening under this category</Button>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeDialog} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.4 }}>
                        {saving ? 'Saving…' : dialog?.mode === 'edit' ? 'Save Changes' : 'Open Vacancy'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(created)} onClose={() => setCreated(null)} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ fontSize: 17, fontWeight: 800 }}>{created?.title} — careers page</DialogTitle>
                <DialogContent>
                    {created && (
                        created.published === false ? (
                            <Alert severity="warning" sx={{ borderRadius: '9px' }}>
                                {created.message || "Vacancy saved, but not published. Set the company's website address in Careers settings, then publish it."}
                            </Alert>
                        ) : (
                            <Stack spacing={1.2}>
                                {created.verified === false && (
                                    <Alert severity="warning" sx={{ borderRadius: '9px' }}>{created.message || 'Published, but not visible yet. Check that its status is Open.'}</Alert>
                                )}
                                {created.siteConfigured === false ? (
                                    <Alert severity="warning" sx={{ borderRadius: '9px' }}>Set your website address in Careers settings to get shareable links.</Alert>
                                ) : (
                                    <>
                                        {created.verified !== false && <Alert severity="success" sx={{ borderRadius: '9px' }}>{created.message || 'Vacancy is live on the careers page.'}</Alert>}
                                        <CareersLinks links={{ liveUrl: created.liveUrl, linkedIn: created.jobBoardLinks?.linkedIn, naukri: created.jobBoardLinks?.naukri }} onCopied={() => notify('Link copied.')} />
                                    </>
                                )}
                            </Stack>
                        )
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCreated(null)} sx={{ ...solidBtn, height: 38, px: 2.2 }}>Done</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirm)}
                onClose={() => { if (!deleting) setConfirm(null); }}
                onConfirm={doDelete}
                title="Remove this vacancy?"
                body={confirm ? `${confirm.title} will be removed, along with its ${confirm.applicants} candidate(s) and every interview scheduled against it. This cannot be undone.` : ''}
                confirmLabel="Remove vacancy"
            />

            <Snackbar open={Boolean(snack)} autoHideDuration={3400} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}

function salaryBand(v) {
    if (!v.minSalary && !v.maxSalary) return '';
    if (v.minSalary && v.maxSalary) return `${inr(v.minSalary)} – ${inr(v.maxSalary)}`;
    return inr(v.minSalary || v.maxSalary);
}
